/**
 * Custom-prompt categories/subcategories domain (manageprompt.*,
 * prompts_category.*, prompts_sub_category.*) - built on apiClient via
 * useRequest, not useAppServices(). Split out of queries/prompts.js: this
 * sub-area (category + subcategory CRUD, plus the manageprompt lookups they
 * depend on) is sizable and conceptually distinct from the partner/admin
 * task-list hooks over there. Shared by:
 *   - customPrompts/PromptCategory/Edit.js, customPrompts/PromptSubCategory/
 *     {Create,Edit}.js -> useAgencyPromptCategories + useAgencySubCategories
 *   - customPrompts/PromptSubCategory/Create.js -> useCreatePromptCategory
 *   - customPrompts/Subcategory.js -> useUpdatePromptSubCategory +
 *     useDeletePromptSubCategory
 *   - UiPrompts/index.js -> useCreateManagePrompt
 *   - customPrompts/Prompts.js, promptlabels/CustomPrompt.js ->
 *     useManagePromptDetails
 *   - promptlabels/ModelPrompts/Edit.js -> usePromptCategoriesByIds (a
 *     differently-shaped sibling of useAgencyPromptCategories - same
 *     endpoint, filtered by category ids instead of agency id, returning the
 *     raw {data, selectedcategory} response instead of just the array)
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useRequest from "hook/useRequest";
import { ReqMethods } from "enums";

const { GET, POST, PUT, DELETE } = ReqMethods;

export const promptCategoryKeys = {
  all: ["prompt-categories"],
  byAgency: (agencyId) => ["prompt-categories", agencyId],
};

export const promptSubCategoryKeys = {
  all: ["prompt-subcategories"],
  byCategory: (categoryId) => ["prompt-subcategories", categoryId],
};

export function useAgencyPromptCategories(agencyId, options = {}) {
  const request = useRequest();
  return useQuery({
    queryKey: promptCategoryKeys.byAgency(agencyId),
    queryFn: async () => {
      const { response } = await request(POST, "/manageprompt/allcategory", {
        payload: { agency_id: agencyId },
      });
      return response?.data || [];
    },
    enabled: !!agencyId,
    ...options,
  });
}

// Same endpoint as useAgencyPromptCategories, but filtered by a list of
// category ids (agency_id always null) and returning the full response
// object - promptlabels/ModelPrompts/Edit.js reads both `.data` and
// `.selectedcategory` off it, not just a plain list.
export function usePromptCategoriesByIds(categoryIds, options = {}) {
  const request = useRequest();
  return useQuery({
    queryKey: ["prompt-categories", (categoryIds || []).join(",")],
    queryFn: async () => {
      const { response } = await request(POST, "/manageprompt/allcategory", {
        payload: { agency_id: null, categories: categoryIds },
      });
      return response || {};
    },
    ...options,
  });
}

export function useAgencySubCategories(categoryId, options = {}) {
  const request = useRequest();
  return useQuery({
    queryKey: promptSubCategoryKeys.byCategory(categoryId),
    queryFn: async () => {
      const { response } = await request(POST, "/manageprompt/selectedsubcategory", {
        payload: { category: categoryId },
      });
      return response?.data || [];
    },
    enabled: !!categoryId,
    ...options,
  });
}

export function useCreatePromptCategory() {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => request(POST, "/prompts/category", { payload }),
    // Bare prefix - matches the old AppService-era invalidate at this same
    // call site, which refreshes every agency's cached category list.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: promptCategoryKeys.all }),
  });
}

export function useUpdatePromptSubCategory() {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => request(PUT, "/prompts/subcategory", { payload }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: promptSubCategoryKeys.all }),
  });
}

export function useDeletePromptSubCategory() {
  const request = useRequest();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) =>
      request(DELETE, "/prompts/subcategory/delete", { query: `_id=${id}`, toaster: true }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: promptSubCategoryKeys.all }),
  });
}

// UiPrompts/index.js's "Add Task" (custom/agency prompt) create. The old
// call site never invalidated a query key either - it force-remounts its
// tab children via a local refresh() instead, so no onSuccess is added here.
export function useCreateManagePrompt() {
  const request = useRequest();
  return useMutation({
    mutationFn: (payload) => request(POST, "/manageprompt", { payload }),
  });
}

// On-demand single-prompt fetch (populates a view/edit modal on click) -
// useMutation rather than useQuery for the same reason as
// prompts.js's useCustomTaskAssistant: it's imperative, not reactive.
export function useManagePromptDetails() {
  const request = useRequest();
  return useMutation({
    mutationFn: ({ agencyId, promptId }) =>
      request(GET, "/manageprompt/getprompts", {
        query: `agency_id=${agencyId}&prompt_id=${promptId}`,
      }),
  });
}
